import CommonTitleBar from "components/common/CommonTitleBar";
import DeleteConfirmationModal from "components/common/ConfirmationDialog";
import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";
import IsLoading from "components/common/IsLoading";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import SingleSelect from "components/common/SingleSelect";
import Spinners from "components/common/Spinners";
import {
  formatDate,
  formatFilterOptions,
  getMinMaxDate,
  handleStopPropagation,
} from "components/common/Utils";
import { addUserSchema } from "components/common/Validation";
import { addUserValues } from "components/constants/InitialFormValues";
import { Form, Formik } from "formik";
import { Ellipsis, X } from "lucide-react";
import { InputSwitch } from "primereact/inputswitch";
import React, { useEffect, useState } from "react";
import {
  Badge,
  Col,
  Container,
  Dropdown,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getRolesList } from "store/actions/rolesSlice";
import {
  addUser,
  deleteUser,
  getAllUsers,
  getUserDetails,
  updateUser,
  updateUserStatus,
} from "store/actions/userSlice";
import { showAlert } from "store/reducers/alertReducer";
import Filters from "components/common/Filters";
import { orderByOptions, statusOptions } from "components/constants/StatusKeys";
import MultipleSelect from "components/common/MultipleSelect";
import { getBranchList } from "store/actions/branchSlice";
import TableShimmer from "components/ShimmerEffects/CommonShimmer/TableShimmer";

const Users = () => {
  const { t } = useTranslation();
  document.title = t("Users | Work Space");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { search } = useLocation();
  const pageNumber = Number(new URLSearchParams(search).get("page")) || 1;

  const {
    isLoading,
    loading,
    perPage,
    totalCount,
    totalPages,
    currentPage,
    users,
    userDetails,
    gettingDetails,
  } = useSelector((state) => state.Users);

  const { rolesList } = useSelector((state) => state.Roles);
  const { branchesList } = useSelector((state) => state.Branches);

  const [selectedId, setSelectedId] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [showModal, setShowModal] = useState(false); // DELETE MODAL
  const [modalType, setModalType] = useState(null);
  const [show, setShow] = useState(false); // ADD/EDIT MODAL

  const [formValues, setFormValues] = useState(addUserValues);

  const [dates, setDates] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    status: -1,
    orderBy: -1,
    role: null,
  });

  useEffect(() => {
    getAllData();
    dispatch(getRolesList());
    dispatch(getBranchList());
  }, []);

  const getAllData = (page, action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD
    const { orderBy, role, status } = selectedFilters;

    const isReset = actionType === "RESET";
    const payload = {
      perPage,
      pageNo: page || pageNumber,
      status: isReset ? -1 : status,
      orderBy: isReset ? -1 : orderBy,
      dateRange: isReset ? {} : getMinMaxDate(dates),
      keyword: ["RESET", "RESET_KEYWORD"].includes(actionType)
        ? ""
        : keyword || "",
      role: !isReset && role ? [role] : [],
    };
    dispatch(getAllUsers(payload));
    navigate(`/users?page=${page || pageNumber}`);
  };

  const handleShow = () => {
    setShow(true);
  };
  const handleClose = () => {
    setShow(false);
    setModalType(null);
    setFormValues(addUserValues);
  };

  useEffect(() => {
    const getDetails = () => {
      const payload = {
        id: selectedId,
      };
      dispatch(getUserDetails(payload));
    };
    if (modalType === "EDIT") {
      getDetails();
    }
  }, [modalType]);

  // SET FORM FIELD
  useEffect(() => {
    if (userDetails && !gettingDetails) {
      const { name, email, role, branches } = userDetails || {};
      setFormValues({
        name,
        email,
        role: role?._id,
        branches,
        accountType: "",
        phoneNumber: "",
        password: "",
        confirmPassword: "",
      });
    }
  }, [userDetails, gettingDetails]);

  const handleStatusChange = (id, status) => {
    setSelectedId(id);
    const payload = {
      id,
      status: status === 1 ? 0 : 1,
    };

    dispatch(updateUserStatus(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleDelete = () => {
    const payload = {
      id: selectedId,
      status: "2", // 2 FOR DELETE
    };
    dispatch(deleteUser(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setShowModal(false);
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleReset = () => {
    setKeyword("");
    setDates([]);
    setSelectedFilters({ orderBy: -1, role: null, status: -1 });
    getAllData(pageNumber, { actionType: "RESET" });
  };

  const handleSubmit = (values, { resetForm }) => {
    const { name, email, branches, role } = values;
    const payload = {
      name,
      email,
      phoneNumber: "",
      role,
      branches,
    };

    if (modalType === "ADD") {
      dispatch(addUser(payload))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            handleClose();
            resetForm();
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    } else if (modalType === "EDIT") {
      const updatedPayload = {
        id: selectedId,
        name,
        email,
        phoneNumber: "",
        role,
        branches,
      };
      dispatch(updateUser(updatedPayload))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            handleClose();
            resetForm();
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    }
  };

  const filters = [
    {
      key: "status",
      placeholder: "Status",
      options: statusOptions,
      type: "singleSelect",
    },
    {
      key: "orderBy",
      placeholder: "Order By",
      options: orderByOptions,
      type: "singleSelect",
    },
    {
      key: "role",
      placeholder: "Select Role",
      options: formatFilterOptions(rolesList),
      type: "singleSelect",
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content common_page">
        <Container fluid>
          <CommonTitleBar
            title={t("Users")}
            isCreateBtn={true}
            createBtnText={t("Add New")}
            onCreateBtnClick={() => {
              setModalType("ADD");
              handleShow();
            }}
          />
          <div className="common_wrapper">
            <Filters
              keyword={keyword}
              setKeyword={setKeyword}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              dates={dates}
              setDates={setDates}
              filters={filters}
              onSearch={getAllData}
              onReset={handleReset}
            />
            <Row>
              <Col lg={12}>
                {loading ? (
                  <TableShimmer col={6} />
                ) : users.length === 0 ? (
                  <NoDataFound text={t("No users found")} />
                ) : (
                  <div
                    className="common_table_style"
                    style={{ minHeight: showDropdown ? 250 : "unset" }}
                    
                  >
                    <Table>
                      <thead className="t_head">
                        <tr>
                          <th>{t("User")}</th>
                          <th>{t("Email")}</th>
                          <th>{t("Role")}</th>
                          <th>{t("Created At")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users?.map((item, index) => (
                          <tr key={item?._id}>
                            <td>{item.name}</td>
                            <td>{item.email}</td>
                            <td>{item?.role?.name || "-"}</td>
                            <td>{formatDate(item?.createdAt)}</td>
                            <td>
                              <Badge
                                bg={
                                  item.status === 1
                                    ? " bg_primary"
                                    : " bg-secondary"
                                }
                              >
                                {item.status === 1
                                  ? t("Active")
                                  : t("Deactive")}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <InputSwitch
                                  checked={item.status === 1}
                                  disabled={
                                    isLoading && selectedId === item._id
                                  }
                                  onChange={(e) =>
                                    handleStatusChange(item._id, item.status)
                                  }
                                />
                                <Dropdown
                                  className="custom_dropdown list_action_dropdown"
                                  drop={"down"}
                                  onClick={handleStopPropagation}
                                  autoClose="outside"
                                  onToggle={(isOpen) => setShowDropdown(isOpen)}
                                >
                                  <Dropdown.Toggle>
                                    <Ellipsis className="text_black" />
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu align="end">
                                    <Dropdown.Item
                                      href="#"
                                      onClick={() => {
                                        navigate(
                                          `/users/user-details?id=${item?._id}`
                                        );
                                      }}
                                    >
                                      {t("View")}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      href="#"
                                      onClick={() => {
                                        setSelectedId(item._id);
                                        setModalType("EDIT");
                                        handleShow();
                                      }}
                                    >
                                      {t("Edit")}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      href="#"
                                      className="text_danger"
                                      onClick={() => {
                                        setSelectedId(item._id);
                                        setShowModal(true);
                                      }}
                                    >
                                      {t("Delete")}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Col>
            </Row>
          </div>
          {!loading && (
            <Paginations
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                getAllData(page);
              }}
              totalCount={totalCount}
            />
          )}
        </Container>
      </div>
      <DeleteConfirmationModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedId(null);
        }}
        onConfirm={handleDelete}
        message={t("Are you sure you want to delete this {{name}}?", {
          name: "User",
        })}
        isLoading={isLoading}
      />

      {/* MODAL FOR ADD/UPDATE USER */}
      <Modal centered show={show} onHide={handleClose} className="common_modal">
        <div className="modal_header g_between">
          <h2 className="fs_17">
            {modalType === "ADD" ? t("Add User") : t("Edit User")}
          </h2>
          <X
            onClick={handleClose}
            className="text_black c_p"
            width={20}
            height={20}
          />
        </div>
        <div className="modal_body mt-0">
          {modalType === "EDIT" && gettingDetails ? (
            <IsLoading />
          ) : (
            <div>
              <Formik
                initialValues={formValues}
                validationSchema={addUserSchema(modalType)}
                enableReinitialize={true}
                onSubmit={handleSubmit}
              >
                {({ setFieldValue, values, errors, touched }) => (
                  <Form>
                    <Row>
                      <Col lg={12}>
                        <div className="form_field mt_13">
                          <CustomLabel htmlFor="name" label={t("Name")} />
                          <CustomField
                            name="name"
                            type="text"
                            placeholder={t("Name")}
                            touched={touched}
                            errors={errors}
                          />
                        </div>
                      </Col>
                      <Col lg={12}>
                        <div className="form_field">
                          <CustomLabel htmlFor="email" label={t("Email")} />
                          <CustomField
                            name="email"
                            type="email"
                            placeholder={t("Email")}
                            touched={touched}
                            errors={errors}
                            isReadOnly={modalType === "EDIT"}
                          />
                        </div>
                      </Col>
                      <Col lg={12}>
                        <div className="form_field">
                          <CustomLabel htmlFor="role" label={t("Role")} />
                          <SingleSelect
                            options={rolesList?.map((item) => ({
                              name: item?.name,
                              value: item?._id,
                            }))}
                            value={values.role}
                            placeholder={t("Role Type")}
                            handleSingleSelectChange={(e) => {
                              setFieldValue("role", e.value);
                            }}
                            className={`${
                              errors.role && touched.role ? "invalid" : ""
                            }`}
                            error={errors?.role}
                          />
                        </div>
                      </Col>
                      <Col lg={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="branches"
                            label={t("Select Branches")}
                          />
                          <MultipleSelect
                            options={branchesList?.map((item) => ({
                              name: item?.name,
                              value: item?._id,
                            }))}
                            value={values.branches}
                            placeholder={t("Select Branches")}
                            handleMultipleSelectChange={(e) => {
                              setFieldValue(`branches`, e);
                            }}
                          />
                        </div>
                      </Col>
                      {/* {modalType === "ADD" && (
                        <>
                          <Col lg={12}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="email"
                                label={t("Password")}
                              />
                              <CustomField
                                name="password"
                                type="password"
                                placeholder={t("Password")}
                                touched={touched}
                                errors={errors}
                              />
                            </div>
                          </Col>
                          <Col lg={12}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="confirmPassword"
                                label={t("Confirm Password")}
                              />
                              <CustomField
                                name="confirmPassword"
                                type="password"
                                placeholder={t("Confirm Password")}
                                touched={touched}
                                errors={errors}
                              />
                            </div>
                          </Col>
                        </>
                      )} */}
                      <Col lg={12}>
                        <div className="g_between mt-4 gap-3">
                          <button
                            type="button"
                            className="cancel_btn w-50"
                            onClick={handleClose}
                          >
                            {t("Cancel")}
                          </button>
                          <button
                            type="submit"
                            className="main_btn w-50"
                            disabled={isLoading}
                          >
                            {modalType === "ADD" ? t("Add") : t("Save")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </div>
          )}
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default Users;
